package fr.manyverse.app.presentation.layout

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.material3.Badge
import androidx.compose.material3.BadgedBox
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.NavigationBar
import androidx.compose.material3.NavigationBarItem
import androidx.compose.material3.NavigationBarItemDefaults
import androidx.compose.material3.NavigationRail
import androidx.compose.material3.NavigationRailItem
import androidx.compose.material3.NavigationRailItemDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.VerticalDivider
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.compositeOver
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.dp

data class NavigationItem(
    val label: String,
    val icon: ImageVector,
    val selectedIcon: ImageVector = icon,
    val selectedIconRotationDegrees: Float = 0f,
    val badgeCount: Int = 0
)

/**
 * [onNavigationPaddingChanged] : marges occupées par la barre (bas) ou le rail (droite) de
 * navigation, pour placer hors du scaffold un contenu aligné sur [overlayContent] (à combiner
 * avec `statusBarsPadding()`).
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GameScreenScaffold(
    navigationItems: List<NavigationItem>,
    selectedTabIndex: Int,
    onTabSelected: (Int) -> Unit,
    overlayContent: @Composable BoxScope.() -> Unit = {},
    onNavigationPaddingChanged: (PaddingValues) -> Unit = {},
    content: @Composable BoxScope.() -> Unit
) {
    WindowSizeContainer(modifier = Modifier.fillMaxSize()) { windowWidth ->
        val useRail = windowWidth != WindowWidth.COMPACT
        val density = LocalDensity.current
        var navRailWidth by remember { mutableStateOf(0.dp) }
        var navBarHeight by remember { mutableStateOf(0.dp) }
        val currentOnNavigationPaddingChanged by rememberUpdatedState(onNavigationPaddingChanged)
        LaunchedEffect(useRail, navRailWidth, navBarHeight) {
            currentOnNavigationPaddingChanged(
                PaddingValues(
                    end = if (useRail) navRailWidth else 0.dp,
                    bottom = if (!useRail) navBarHeight else 0.dp
                )
            )
        }

        Surface(
            modifier = Modifier.fillMaxSize(),
            color = MaterialTheme.colorScheme.background
        ) {
            // Dégradé descendant dérivé de la couleur primaire (renforce l'identité de
            // l'univers), posé avant statusBarsPadding pour s'étendre derrière la barre d'état.
            val background = MaterialTheme.colorScheme.background
            val gradientTop = MaterialTheme.colorScheme.primary.copy(alpha = 0.16f)
                .compositeOver(background)
            val backgroundGradient = remember(gradientTop, background) {
                Brush.verticalGradient(
                    colorStops = arrayOf(
                        0f to gradientTop,
                        0.35f to background,
                        1f to background
                    )
                )
            }
            Box(modifier = Modifier.fillMaxSize().background(backgroundGradient).statusBarsPadding()) {
                // Content — stable position 0 in Box, state preserved across orientation changes
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(
                            end = if (useRail) navRailWidth else 0.dp,
                            bottom = if (!useRail) navBarHeight else 0.dp
                        )
                        .padding(16.dp)
                ) {
                    content()
                }

                // Overlay — stable position 1 in Box, nav padding only (no 16dp)
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(
                            end = if (useRail) navRailWidth else 0.dp,
                            bottom = if (!useRail) navBarHeight else 0.dp
                        ),
                    content = overlayContent
                )

                // Navigation — position 2, changes between rail/bar without affecting content above
                if (useRail) {
                    Row(
                        modifier = Modifier
                            .fillMaxHeight()
                            .align(Alignment.CenterEnd)
                            .onGloballyPositioned { coords ->
                                navRailWidth = with(density) { coords.size.width.toDp() }
                            }
                    ) {
                        VerticalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        NavigationRail(
                            modifier = Modifier.fillMaxHeight(),
                            containerColor = MaterialTheme.colorScheme.surfaceContainer
                        ) {
                            navigationItems.forEachIndexed { index, item ->
                                val selected = selectedTabIndex == index
                                NavigationRailItem(
                                    selected = selected,
                                    onClick = { onTabSelected(index) },
                                    icon = {
                                        BadgedBox(
                                            badge = {
                                                if (item.badgeCount > 0) Badge(
                                                    containerColor = MaterialTheme.colorScheme.error,
                                                    modifier = Modifier.size(16.dp)
                                                )
                                            }) {
                                            Icon(
                                                imageVector = if (selected) item.selectedIcon else item.icon,
                                                contentDescription = item.label,
                                                modifier = Modifier.rotate(
                                                    if (selected) item.selectedIconRotationDegrees else 0f
                                                )
                                            )
                                        }
                                    },
                                    label = { Text(item.label) },
                                    colors = NavigationRailItemDefaults.colors(
                                        selectedIconColor = MaterialTheme.colorScheme.primary,
                                        selectedTextColor = MaterialTheme.colorScheme.primary,
                                        indicatorColor = MaterialTheme.colorScheme.primaryContainer
                                    )
                                )
                            }
                        }
                    }
                } else {
                    Column(
                        modifier = Modifier
                            .align(Alignment.BottomCenter)
                            .onGloballyPositioned { coords ->
                                navBarHeight = with(density) { coords.size.height.toDp() }
                            }
                    ) {
                        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                        NavigationBar(
                            containerColor = MaterialTheme.colorScheme.surfaceContainer
                        ) {
                            navigationItems.forEachIndexed { index, item ->
                                val selected = selectedTabIndex == index
                                NavigationBarItem(
                                    selected = selected,
                                    onClick = { onTabSelected(index) },
                                    icon = {
                                        BadgedBox(
                                            badge = {
                                                if (item.badgeCount > 0) Badge(
                                                    containerColor = MaterialTheme.colorScheme.error,
                                                    modifier = Modifier.size(16.dp)
                                                )
                                            }) {
                                            Icon(
                                                imageVector = if (selected) item.selectedIcon else item.icon,
                                                contentDescription = item.label,
                                                modifier = Modifier.rotate(
                                                    if (selected) item.selectedIconRotationDegrees else 0f
                                                )
                                            )
                                        }
                                    },
                                    label = { Text(item.label) },
                                    colors = NavigationBarItemDefaults.colors(
                                        selectedIconColor = MaterialTheme.colorScheme.primary,
                                        selectedTextColor = MaterialTheme.colorScheme.primary,
                                        indicatorColor = MaterialTheme.colorScheme.primaryContainer
                                    )
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
